import { useQuery } from '@tanstack/react-query';
import { supabase, tables } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { startOfMonth } from 'date-fns/startOfMonth';
import { endOfMonth } from 'date-fns/endOfMonth';
import { subMonths } from 'date-fns/subMonths';

interface DashboardMetrics {
  revenue: {
    currentMonth: number;
    previousMonth: number;
    percentageChange: number;
  };
  students: {
    total: number;
    newThisMonth: number;
    percentageChange: number;
  };
  attendance: {
    rate: number;
    previousRate: number;
    percentageChange: number;
  };
  classCapacity: {
    rate: number;
    previousRate: number;
    percentageChange: number;
  };
  activeStudents?: number;
  freeTrialStudents?: number;
  trialConversionRate?: number;
}

export const useDashboardData = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;

  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['dashboardMetrics', organizationId],
    queryFn: async (): Promise<DashboardMetrics> => {
      const now = new Date();
      const currentMonthStart = startOfMonth(now);
      const currentMonthEnd = endOfMonth(now);
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      const lastMonthEnd = endOfMonth(subMonths(now, 1));

      // Run all independent queries in parallel
      const [
        currentMonthPaymentsRes,
        previousMonthPaymentsRes,
        totalStudentsRes,
        newStudentsRes,
        lastMonthNewStudentsRes,
        currentAttendanceRes,
        previousAttendanceRes,
        classesRes,
        enrollmentsRes,
        activeEnrollmentsRes
      ] = await Promise.all([
        supabase
          .from('payments')
          .select('amount_cents')
          .eq('organization_id', organizationId)
          .gte('paid_at', currentMonthStart.toISOString())
          .lte('paid_at', currentMonthEnd.toISOString()),
        supabase
          .from('payments')
          .select('amount_cents')
          .eq('organization_id', organizationId)
          .gte('paid_at', lastMonthStart.toISOString())
          .lte('paid_at', lastMonthEnd.toISOString()),
        supabase
          .from('students')
          .select('id', { count: 'exact' })
          .eq('organization_id', organizationId),
        supabase
          .from('students')
          .select('id', { count: 'exact' })
          .eq('organization_id', organizationId)
          .gte('created_at', currentMonthStart.toISOString()),
        supabase
          .from('students')
          .select('id', { count: 'exact' })
          .eq('organization_id', organizationId)
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', lastMonthEnd.toISOString()),
        supabase
          .from('class_attendance')
          .select('status')
          .eq('organization_id', organizationId)
          .gte('date', currentMonthStart.toISOString())
          .lte('date', currentMonthEnd.toISOString()),
        supabase
          .from('class_attendance')
          .select('status')
          .eq('organization_id', organizationId)
          .gte('date', lastMonthStart.toISOString())
          .lte('date', lastMonthEnd.toISOString()),
        supabase
          .from('classes')
          .select('id, max_students')
          .eq('organization_id', organizationId)
          .eq('is_active', true),
        supabase
          .from('enrollments')
          .select('class_id')
          .eq('organization_id', organizationId)
          .eq('status', 'active'),
        supabase
          .from('enrollments')
          .select('student_id')
          .eq('organization_id', organizationId)
          .eq('status', 'active')
      ]);

      const currentMonthPayments = currentMonthPaymentsRes.data || [];
      const previousMonthPayments = previousMonthPaymentsRes.data || [];
      const totalStudents = totalStudentsRes.data;
      const newStudents = newStudentsRes.data;
      const lastMonthNewStudents = lastMonthNewStudentsRes.data;
      const currentAttendance = currentAttendanceRes.data;
      const previousAttendance = previousAttendanceRes.data;
      const classes = classesRes.data;
      const enrollments = enrollmentsRes.data;
      const activeEnrollments = activeEnrollmentsRes.data;

      // Calculate revenues
      const currentMonthRevenue = currentMonthPayments.reduce((sum, p) => sum + (p.amount_cents || 0), 0);
      const previousMonthRevenue = previousMonthPayments.reduce((sum, p) => sum + (p.amount_cents || 0), 0);

      // Calculate attendance rates
      const calculateAttendanceRate = (attendance: any[]) => {
        if (!attendance || attendance.length === 0) return 0;
        const present = attendance.filter(a => a.status === 'present').length;
        return (present / attendance.length) * 100;
      };

      const currentAttendanceRate = calculateAttendanceRate(currentAttendance || []);
      const previousAttendanceRate = calculateAttendanceRate(previousAttendance || []);

      // Calculate class capacity
      const calculateCapacityRate = () => {
        if (!classes || !enrollments) return 0;
        const classEnrollments = classes.map(c => ({
          max: c.max_students || 0,
          current: enrollments.filter(e => e.class_id === c.id).length
        }));
        
        const totalCapacity = classEnrollments.reduce((sum, c) => sum + c.max, 0);
        const totalEnrolled = classEnrollments.reduce((sum, c) => sum + c.current, 0);
        
        return totalCapacity > 0 ? (totalEnrolled / totalCapacity) * 100 : 0;
      };

      const currentCapacityRate = calculateCapacityRate();
      const previousCapacityRate = currentCapacityRate; // Historical capacity not implemented yet

      // Fetch active students (students with at least one active enrollment)
      const activeStudentIds = Array.from(new Set((activeEnrollments || []).map(e => e.student_id)));


      return {
        revenue: {
          currentMonth: currentMonthRevenue,
          previousMonth: previousMonthRevenue,
          percentageChange: calculatePercentageChange(currentMonthRevenue, previousMonthRevenue)
        },
        students: {
          total: totalStudents?.length || 0,
          newThisMonth: newStudents?.length || 0,
          percentageChange: calculatePercentageChange(
            newStudents?.length || 0,
            lastMonthNewStudents?.length || 0
          )
        },
        attendance: {
          rate: currentAttendanceRate,
          previousRate: previousAttendanceRate,
          percentageChange: calculatePercentageChange(currentAttendanceRate, previousAttendanceRate)
        },
        classCapacity: {
          rate: currentCapacityRate,
          previousRate: previousCapacityRate,
          percentageChange: calculatePercentageChange(currentCapacityRate, previousCapacityRate)
        },
        activeStudents: activeStudentIds.length
      };
    },
    enabled: !!organizationId,
    // Original cache settings (for slower updates):
    // staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    
    // Updated cache settings (for faster updates):
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true // Always refetch on mount
  });

  return {
    metrics,
    isLoading: metricsLoading
  };
};
